import { useMemo } from 'react';

import { AuthStatus } from '@/enums';
import { NavigationGuardStatus } from '@/components/navigation-stack-guard';
import {
  NetworkStatus,
  useNetworkStatus,
} from '@/contexts/network-status-context';
import { useAppMinimumVersion } from './use-app-minimum-version';
import { useAuth } from '@/contexts/auth-context';
import { VersionSupportStatus } from '@/utils/minimum-version-types';

/**
 * Calculates the navigation guard status based on authentication state, network connectivity,
 * and app version requirements.
 *
 * This hook reads directly from AuthContext (not UserContext), making it usable in any experience
 * (operator portal, sales portal, etc.) without depending on restaurant-scoped UserContext data.
 *
 * The returned status determines which screens the NavigationStackGuard component will allow
 * users to access:
 * - AUTHENTICATED / OFFLINE_MODE: Full access to the app
 * - NOT_AUTHENTICATED: Shows login screens
 * - NOT_AUTHORIZED: Shows not authorized screen
 * - OFFLINE_SCREEN: Shows offline error screen
 * - APP_STORE_UPGRADE / OVER_THE_AIR_UPGRADE: Shows upgrade prompts
 *
 * @returns NavigationGuardStatus | null - The calculated status, or null if auth state is loading
 *
 * @example
 * ```typescript
 * const navigationGuardStatus = useNavigationGuardStatus();
 * const { appMinimumVersion } = useAppMinimumVersion();
 *
 * if (!navigationGuardStatus) {
 *   return <LogoLoadingIndicator />;
 * }
 *
 * return (
 *   <NavigationStackGuard
 *     appMinimumVersion={appMinimumVersion}
 *     navigationGuardStatus={navigationGuardStatus}
 *   >
 *     <YourStackNavigator />
 *   </NavigationStackGuard>
 * );
 * ```
 */
export function useNavigationGuardStatus(): NavigationGuardStatus | null {
  // Read auth status directly from AuthContext (not UserContext!)
  // This allows any experience to use this hook without coupling to restaurant data
  const authStatus = useAuth((s) => s.authStatus);
  const { networkStatus } = useNetworkStatus();
  const { supportStatus } = useAppMinimumVersion();

  return useMemo<NavigationGuardStatus | null>(() => {
    let navGuardStatus = null;

    // Map auth status to navigation guard status
    switch (authStatus) {
      case AuthStatus.Authenticated:
        navGuardStatus = NavigationGuardStatus.AUTHENTICATED;
        break;
      case AuthStatus.Offline:
        navGuardStatus = NavigationGuardStatus.OFFLINE_MODE;
        break;
      case AuthStatus.NotAuthenticated:
        // If we wanted to display a custom screen when Offline Mode is active,
        // but the Offline Auth is expired, we would do that here
        if (networkStatus === NetworkStatus.DISCONNECTED) {
          navGuardStatus = NavigationGuardStatus.OFFLINE_SCREEN;
        } else if (
          networkStatus === NetworkStatus.CONNECTED ||
          networkStatus === NetworkStatus.UNDETERMINED
        ) {
          // When network status is undetermined during app init, it's better to show login
          // rather than block with loading indefinitely
          navGuardStatus = NavigationGuardStatus.NOT_AUTHENTICATED;
        }
        break;
      case AuthStatus.NotAuthorized:
        navGuardStatus = NavigationGuardStatus.NOT_AUTHORIZED;
        break;
    }

    // App upgrade check should run even if authStatus is null
    // Version requirements take precedence over auth status
    if (supportStatus === VersionSupportStatus.AppStoreUpgrade) {
      navGuardStatus = NavigationGuardStatus.APP_STORE_UPGRADE;
    } else if (supportStatus === VersionSupportStatus.OverTheAirUpgrade) {
      navGuardStatus = NavigationGuardStatus.OVER_THE_AIR_UPGRADE;
    }

    return navGuardStatus;
  }, [authStatus, networkStatus, supportStatus]);
}
